/**
 * The three calls authenticator-app enrolment makes, behind a small interface so
 * the same flow can run with the app's own axios instance (Settings, signed in)
 * or with bare axios and an explicit enrolment token (the sign-in page).
 */

/**
 * `qr_png_base64` is the same secret as an image. It travels in this answer (which
 * the server marks no-store) because an enrolled account's QR route is closed:
 * a replacement authenticator is shown once, here, and never served again.
 */
export type PendingSecret = { secret: string; provisioning_uri: string; qr_png_base64?: string }

export type EnrolmentClient = {
  /** `currentCode` is required by the server only to re-key an enrolled account. */
  setup: (currentCode?: string) => Promise<PendingSecret>
  qrcode: () => Promise<Blob>
  confirm: (code: string) => Promise<unknown>
}

// Method syntax on purpose: axios' own overloads are checked bivariantly, and a
// property-of-function type would reject the real instance for no real reason.
type HttpLike = {
  post(url: string, data?: unknown, config?: Record<string, unknown>): Promise<{ data: unknown }>
  get(url: string, config?: Record<string, unknown>): Promise<{ data: unknown }>
}

/** `base` is the API root when `http` is bare axios, empty for the app's own instance. */
export function enrolmentClient(
  http: HttpLike,
  base = '',
  headers?: Record<string, string>,
): EnrolmentClient {
  const config: Record<string, unknown> = headers ? { headers } : {}
  return {
    setup: async (currentCode) =>
      (await http.post(`${base}/auth/2fa/setup`, currentCode ? { code: currentCode } : {}, config))
        .data as PendingSecret,
    qrcode: async () =>
      (await http.get(`${base}/auth/2fa/qrcode`, { ...config, responseType: 'blob' })).data as Blob,
    confirm: async (code) => (await http.post(`${base}/auth/2fa/confirm`, { code }, config)).data,
  }
}

export function errorDetail(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
  return typeof detail === 'string' && detail.trim() ? detail : fallback
}
